import React, { useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { authApi } from "../../../repositories/auth";
import { handleError } from "../../../utils/errorHandler";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

const ForgetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const initialValues = {
    email: "",
  };

  useEffect(() => {
    const authData = localStorage.getItem("auth");
    if (authData) {
      const auth = JSON.parse(authData);
      if (auth.token) {
        navigate("/dashboard");
      }
    }
  });

  const handleSubmit = async (
    values: { email: string },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const { data } = await authApi.forgetPassword({ email: values.email });

      if (data) {
        Swal.fire({
          title: "Success!",
          text: "Password reset link has been sent to your email.",
          icon: "success",
        });
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="flex items-center justify-center min-h-screen px-4 py-12 bg-neutral-500 sm:px-6 lg:px-8">
      <div className="w-full max-w-md p-8 space-y-8 bg-white shadow-lg rounded-xl">
        <div>
          <h1 className="text-3xl font-bold text-center text-black">
            Forget Password
          </h1>
          <p className="text-sm text-center text-neutral-600">
            Enter your email to receive a password reset link.
          </p>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="mt-8 space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-neutral-700"
                >
                  Email
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  className="block w-full px-4 py-2 mt-1 bg-white border border-neutral-300 rounded-md shadow-md"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="mt-1 text-sm text-danger-600"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? "Sending..." : "Send Reset Link"}
              </Button>

              <div className="text-center">
                Already have an account?
                <Link
                  to="/auth/login"
                  className="text-sm font-medium text-primary-700 hover:text-primary-700"
                >
                  Log in
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </section>
  );
};

export default ForgetPasswordPage;
